import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceArea, ReferenceDot,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Info } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import api from "@/lib/api";
import { fmtPrice } from "@/lib/format";
import { useTranslation } from "@/lib/i18n";

const RANGES = ["1D", "1W", "1M", "3M", "1Y", "5Y"];

const C_GREEN = "#3FAE89";
const C_RED = "#E5484D";
const C_AMBER = "#E0B341";
const C_PRICE = "#F4F5F7";

function modeRate(mode) {
  if (mode === "Growth") return 0.11;
  if (mode === "Speculative") return 0.09;
  return 0.08;
}

function valuationLabel(diffPct) {
  if (diffPct == null) return { text: "Fair value", color: C_AMBER, sign: 0 };
  if (diffPct >= 1.5) return { text: `Undervalued by +${diffPct.toFixed(1)}%`, color: C_GREEN, sign: 1 };
  if (diffPct <= -1.5) return { text: `Overvalued by ${Math.abs(diffPct).toFixed(1)}%`, color: C_RED, sign: -1 };
  return { text: "Fairly valued", color: C_AMBER, sign: 0 };
}

function ChartTooltip({ active, payload, label, fairValueLatest, t }) {
  if (!active || !payload || !payload.length) return null;
  const pricePoint = payload.find((p) => p.dataKey === "price");
  const fvPoint = payload.find((p) => p.dataKey === "fv");
  const price = pricePoint?.value;
  const fv = fvPoint?.value ?? fairValueLatest;
  const diff = (fv && price && Number(price) !== 0)
    ? ((Number(fv) - Number(price)) / Number(price)) * 100 : null;
  // Translate verdict labels
  const verdict = (() => {
    if (diff == null) return { text: t("analyze.chart.legend_fair_value_short"), color: C_AMBER, sign: 0 };
    if (diff >= 1.5) return { text: t("analyze.chart.verdict_undervalued", { pct: diff.toFixed(1) }), color: C_GREEN, sign: 1 };
    if (diff <= -1.5) return { text: t("analyze.chart.verdict_overvalued", { pct: Math.abs(diff).toFixed(1) }), color: C_RED, sign: -1 };
    return { text: t("analyze.chart.verdict_fair"), color: C_AMBER, sign: 0 };
  })();
  return (
    <div className="rounded-lg border border-border/70 bg-popover/95 px-3 py-2 shadow-[var(--app-shadow-elev-1)] min-w-[180px]">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs tabular">
        <span className="text-muted-foreground">{t("analyze.chart.tooltip_price")}</span>
        <span className="font-mono text-right">${fmtPrice(price)}</span>
        {fv != null ? (
          <>
            <span className="text-muted-foreground">{t("analyze.chart.tooltip_fair_value")}</span>
            <span className="font-mono text-right" style={{ color: C_GREEN }}>${fmtPrice(fv)}</span>
          </>
        ) : null}
      </div>
      {diff != null ? (
        <div className="text-[11px] tabular mt-1 font-medium" style={{ color: verdict.color }}>
          {verdict.text}
        </div>
      ) : null}
    </div>
  );
}

/** Custom shape rendered by Recharts on the current-price ReferenceDot. */
function CurrentPriceMarker({ cx, cy, currentPrice, plotRight, compact }) {
  if (cx == null || cy == null) return null;
  const pillW = compact ? 70 : 88;
  const pillH = 22;
  const pillX = Math.min(cx + 12, (plotRight ?? cx + 100) - pillW - 4);
  const priceFontSize = compact ? 10 : 11;
  // Backdrop pill behind "NOW" label so it remains readable when it
  // happens to sit close to the Fair Value pill above (Phase 6.Y polish).
  const nowBadgeW = compact ? 28 : 32;
  const nowBadgeH = 11;
  return (
    <g style={{ pointerEvents: "none" }}>
      {/* outer ring */}
      <circle cx={cx} cy={cy} r={9} fill={C_PRICE} fillOpacity={0.15} />
      {/* solid dot */}
      <circle cx={cx} cy={cy} r={5.5} fill={C_PRICE} stroke="#0A0A0C" strokeWidth={2} />
      <circle cx={cx} cy={cy} r={2} fill="#0A0A0C" />
      {/* short connector to pill */}
      <line x1={cx + 6} y1={cy} x2={pillX} y2={cy} stroke={C_PRICE} strokeOpacity={0.5} strokeWidth={1} />
      {/* pill */}
      <g transform={`translate(${pillX}, ${cy - pillH / 2})`}>
        <rect width={pillW} height={pillH} rx={11} fill="rgba(244,245,247,0.96)" stroke="rgba(10,10,12,0.4)" />
        <text
          x={pillW / 2} y={pillH / 2 + 4}
          textAnchor="middle" fontSize={priceFontSize} fontWeight={700} fill="#0A0A0C"
          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          {`$${fmtPrice(currentPrice)}`}
        </text>
        {/* NOW chip — backed by a small dark badge so it remains legible
            even when overlapping with the Fair Value pill above. */}
        <rect x={2} y={-(nowBadgeH) - 2} width={nowBadgeW} height={nowBadgeH} rx={3} fill="rgba(10,10,12,0.78)" />
        <text
          x={2 + nowBadgeW / 2} y={-3}
          textAnchor="middle"
          fontSize={9} fontWeight={700} fill="rgba(244,245,247,0.95)"
          style={{ fontFamily: "Inter, system-ui", letterSpacing: "0.08em" }}
        >
          NOW
        </text>
      </g>
    </g>
  );
}

/** Custom shape rendered by Recharts on the fair-value ReferenceDot. */
function FairValueMarker({ cx, cy, fairValue, plotRight, compact }) {
  if (cx == null || cy == null) return null;
  const pillW = compact ? 80 : 100;
  const pillH = 22;
  const pillX = Math.min(cx + 12, (plotRight ?? cx + 110) - pillW - 4);
  const priceFontSize = compact ? 10 : 11;
  return (
    <g style={{ pointerEvents: "none" }}>
      {/* large soft halo */}
      <circle cx={cx} cy={cy} r={16} fill={C_GREEN} fillOpacity={0.10}>
        <animate attributeName="r" values="14;19;14" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="fill-opacity" values="0.10;0.18;0.10" dur="2.4s" repeatCount="indefinite" />
      </circle>
      {/* mid halo */}
      <circle cx={cx} cy={cy} r={10} fill={C_GREEN} fillOpacity={0.22} />
      {/* glow ring */}
      <circle cx={cx} cy={cy} r={7} fill="none" stroke={C_GREEN} strokeOpacity={0.85} strokeWidth={1.5} />
      {/* core dot */}
      <circle cx={cx} cy={cy} r={5.5} fill={C_GREEN} stroke="#0A0A0C" strokeWidth={2} />
      {/* connector to pill */}
      <line x1={cx + 6} y1={cy} x2={pillX} y2={cy} stroke={C_GREEN} strokeOpacity={0.7} strokeWidth={1.2} />
      {/* pill */}
      <g transform={`translate(${pillX}, ${cy - pillH / 2})`}>
        <rect width={pillW} height={pillH} rx={11} fill="rgba(63,174,137,0.18)" stroke={C_GREEN} strokeOpacity={0.95} />
        <text
          x={pillW / 2} y={pillH / 2 + 4}
          textAnchor="middle" fontSize={priceFontSize} fontWeight={700} fill={C_GREEN}
          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          {`$${fmtPrice(fairValue)}`}
        </text>
        <text
          x={6} y={-4}
          fontSize={9} fontWeight={700} fill={C_GREEN}
          style={{ fontFamily: "Inter, system-ui", letterSpacing: "0.06em" }}
        >
          FAIR VALUE
        </text>
      </g>
    </g>
  );
}

/**
 * Custom label rendered on the gap ReferenceArea — draws a vertical
 * connector + a centered "% delta" pill, all positioned via the
 * viewBox (x, y, width, height) of the area.
 */
function GapDeltaLabel({ viewBox, color, diffPct }) {
  if (!viewBox || diffPct == null) return null;
  const { x, y, width, height } = viewBox;
  if (height < 1) return null;
  const cx = x + width - 0.5; // hug right edge of gap rect
  const cy = y + height / 2;
  const pillCx = x + width / 2;
  return (
    <g style={{ pointerEvents: "none" }}>
      {/* vertical connector */}
      <line x1={cx} y1={y} x2={cx} y2={y + height} stroke={color} strokeOpacity={0.95} strokeWidth={2.5} />
      {/* small caps */}
      <line x1={cx - 7} x2={cx + 7} y1={y} y2={y} stroke={color} strokeOpacity={0.85} strokeWidth={2} />
      <line x1={cx - 7} x2={cx + 7} y1={y + height} y2={y + height} stroke={color} strokeOpacity={0.85} strokeWidth={2} />
      {/* center delta pill */}
      {height > 26 ? (
        <g>
          <rect
            x={pillCx - 32} y={cy - 11} width={64} height={22} rx={11}
            fill="rgba(10,10,12,0.92)"
            stroke={color}
            strokeOpacity={0.9}
            strokeWidth={1.5}
          />
          <text
            x={pillCx} y={cy + 4}
            textAnchor="middle" fontSize={11} fontWeight={800} fill={color}
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            {`${diffPct >= 0 ? "+" : "−"}${Math.abs(diffPct).toFixed(1)}%`}
          </text>
        </g>
      ) : null}
    </g>
  );
}

export default function PriceChart({ ticker, fairValue, fairLow, fairHigh, mode, fvGrowthRate, valuation, initialRange = "1Y", height = 420 }) {
  const { t } = useTranslation();
  const [range, setRange] = useState(initialRange);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overlay, setOverlay] = useState(true);
  // Phase 6.AX — track chart data availability so we can show a clear
  // fallback message instead of a blank canvas when the historical-price
  // FMP endpoint is plan-restricted or otherwise unavailable. Backend
  // sets these on every /prices response (see backend/routers/stock_data.py).
  const [chartStatus, setChartStatus] = useState("ok");
  const [chartUnavailableReason, setChartUnavailableReason] = useState(null);

  // Lazy-load: only fetch the historical series when the chart actually
  // enters the viewport. Most users see analyze results above the fold,
  // scroll down to the chart later (or never). This avoids burning a
  // historical-data FMP call every time someone opens an analysis page.
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView) return undefined;
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      // Older browsers / SSR — fetch immediately.
      setInView(true);
      return undefined;
    }
    const el = containerRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
          break;
        }
      }
    }, { rootMargin: "200px 0px" }); // prefetch ~one viewport early
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView]);

  // Phase 3.2 mobile refinement — taller, more cinematic chart on small screens.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 639px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Mobile chart proportions — premium finance feel, NOT a tall portrait.
  // Target a ~16:10 to ~4:3 aspect ratio relative to the card width so the
  // chart looks landscape-ish on phones instead of vertically stretched.
  // The chart width on a typical 360–430px viewport is ~320–390px (after
  // card padding & y-axis gutter), so a height of ~210–250px hits the
  // sweet spot. We use viewport width to derive it and clamp tightly so
  // the labels (Fair value / Now pills) remain inside safe bounds.
  const [vw, setVw] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 390));
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const mobileHeight = useMemo(() => {
    // Approx usable plot width = viewport - (page side padding 32) - (card padding 16) - (right label margin 86) - (y-axis 40)
    const usable = Math.max(220, vw - 32 - 16 - 86 - 40);
    // Use a ~10:16 ratio against the FULL card inner width (vw - 48) so the
    // box looks landscape, not portrait. Clamp 210–270 for safety.
    const cardInner = Math.max(280, vw - 48);
    const target = Math.round(cardInner * (10 / 16));
    return Math.min(270, Math.max(210, target, Math.round(usable * 0.55)));
  }, [vw]);
  const effectiveHeight = isMobile ? mobileHeight : height;

  const r = fvGrowthRate != null ? Number(fvGrowthRate) : modeRate(mode);

  useEffect(() => {
    if (!inView) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/stocks/${ticker}/prices`, { params: { range } });
        const formatted = (res.data.series || []).map((p) => ({ t: p.date, price: p.close }));
        if (!cancelled) {
          setSeries(formatted);
          // Capture the new Phase 6.AX telemetry. Default to "ok" when
          // the backend predates this field (graceful upgrade path).
          const status = res.data.chart_data_status || (formatted.length ? "ok" : "unavailable_unknown");
          setChartStatus(status);
          setChartUnavailableReason(res.data.chart_unavailable_reason || null);
          // Phase 6.BH+ FIX — broadcast chart readiness so the Analyze
          // page lifecycle event can stamp the actual chart_points_count
          // and chart_data_status on `last_rendered_analysis`. We use a
          // window event (vs lifting state) because PriceChart is used
          // across multiple pages and we want zero coupling.
          try {
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("stockaily:chart_ready", {
                detail: {
                  ticker,
                  range,
                  points_count: formatted.length,
                  chart_data_status: status,
                },
              }));
            }
          } catch { /* swallow */ }
        }
      } catch (err) {
        if (!cancelled) {
          setSeries([]);
          setChartStatus("unavailable_unknown");
          setChartUnavailableReason("Chart data could not be loaded right now.");
          // Phase 6.BH+ FIX — also broadcast on failure so the lifecycle
          // event still gets a non-null chart status (the user wants
          // Owner Console to never show dashes for chart fields).
          try {
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("stockaily:chart_ready", {
                detail: {
                  ticker,
                  range,
                  points_count: 0,
                  chart_data_status: "unavailable_unknown",
                },
              }));
            }
          } catch { /* swallow */ }
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [ticker, range, inView]);

  const xTickFormatter = useMemo(() => {
    return (v) => {
      if (!v) return "";
      if (range === "1D") return v.slice(11, 16);
      return v.slice(0, 7);
    };
  }, [range]);

  const data = useMemo(() => {
    if (!series.length || !fairValue) return series.map((p) => ({ ...p, fv: null }));
    const lastDateMs = new Date(series[series.length - 1].t).getTime();
    const yearMs = 365.25 * 24 * 3600 * 1000;
    return series.map((p) => {
      const ms = new Date(p.t).getTime();
      const yearsDiff = (ms - lastDateMs) / yearMs;
      const fv = Number(fairValue) * Math.pow(1 + r, yearsDiff);
      return { ...p, fv };
    });
  }, [series, fairValue, r]);

  const currentPrice = useMemo(() => series.length ? Number(series[series.length - 1].price) : null, [series]);
  const earliestFv = useMemo(() => {
    if (!data.length || !fairValue) return null;
    return Number(data[0].fv);
  }, [data, fairValue]);

  const [yMin, yMax] = useMemo(() => {
    if (!series.length) return [0, 1];
    const lows = series.map((d) => d.price).filter(Number.isFinite);
    const candidates = [
      ...lows,
      fairValue, fairLow, fairHigh, currentPrice, earliestFv,
    ].filter((v) => v != null && Number.isFinite(Number(v))).map(Number);
    const min = Math.min(...candidates);
    const max = Math.max(...candidates);
    const span = max - min || 1;
    const pad = span * 0.12;
    const niceStep = Math.pow(10, Math.floor(Math.log10(span / 5)));
    const niceMin = Math.floor((min - pad) / niceStep) * niceStep;
    const niceMax = Math.ceil((max + pad) / niceStep) * niceStep;
    return [niceMin, niceMax];
  }, [series, fairValue, fairLow, fairHigh, currentPrice, earliestFv]);

  const diffPct = useMemo(() => {
    if (currentPrice == null || !fairValue || Number(currentPrice) === 0) return null;
    return ((Number(fairValue) - Number(currentPrice)) / Number(currentPrice)) * 100;
  }, [currentPrice, fairValue]);
  const verdict = (() => {
    if (diffPct == null) return { text: t("analyze.chart.legend_fair_value_short"), color: C_AMBER, sign: 0 };
    if (diffPct >= 1.5) return { text: t("analyze.chart.verdict_undervalued", { pct: diffPct.toFixed(1) }), color: C_GREEN, sign: 1 };
    if (diffPct <= -1.5) return { text: t("analyze.chart.verdict_overvalued", { pct: Math.abs(diffPct).toFixed(1) }), color: C_RED, sign: -1 };
    return { text: t("analyze.chart.verdict_fair"), color: C_AMBER, sign: 0 };
  })();
  // Pure mathematical valuation tone (used for zone tint labels)
  const isUnder = verdict.sign === 1;
  const isOver = verdict.sign === -1;
  // Display color follows the signal so the chip stays consistent with the
  // AI Opportunity Score across the app (a "Undervalued by 19%" value-trap
  // shows as Neutral/yellow instead of green).
  const signalKey = valuation?.signal;
  const diffColor = signalKey === "Opportunity" ? C_GREEN
    : signalKey === "Risk" ? C_RED
    : signalKey === "Neutral" ? C_AMBER
    : verdict.color;

  const lastT = series.length ? series[series.length - 1].t : null;
  // For the gap rectangle: pick a date roughly 8% of the dataset back.
  const gapStartT = useMemo(() => {
    if (!data.length) return null;
    const idx = Math.max(0, data.length - 1 - Math.max(2, Math.floor(data.length * 0.08)));
    return data[idx].t;
  }, [data]);

  const gapColor = diffColor;
  const gapGradientId = isUnder ? "gap-under" : isOver ? "gap-over" : "gap-fair";

  return (
    <div ref={containerRef} className="rounded-2xl border border-border/70 bg-card/60 p-2 sm:p-4 shadow-[var(--app-shadow-elev-1)]" data-testid="price-chart-card">
      {/* === Header: title + (desktop-only) controls ===
          On mobile the controls drop BELOW the chart so the title + verdict
          breathe properly before the chart itself. */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5">
            <div className="text-sm sm:text-sm font-medium">{t("analyze.chart.title")}</div>
            <TooltipProvider delayDuration={150}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label={t("analyze.chart.info_aria")} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="price-chart-info-trigger">
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {t("analyze.chart.info_body")}
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          </div>
          {/* Subtitle: visible on desktop; collapsed on mobile to reduce clutter */}
          <div className="hidden sm:block text-xs text-muted-foreground">{t("analyze.chart.subtitle")}</div>
        </div>
        {/* Desktop controls: timeframe + overlay toggle inline with title */}
        <div className="hidden sm:flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="fv-overlay" className="text-xs text-muted-foreground">{t("analyze.chart.overlay_toggle")}</Label>
            <Switch id="fv-overlay" checked={overlay} onCheckedChange={setOverlay} data-testid="fair-value-overlay-switch" />
          </div>
          <Tabs value={range} onValueChange={setRange} data-testid="price-timeframe-tabs">
            <TabsList className="bg-muted/30 rounded-lg p-1">
              {RANGES.map((rg) => (
                <TabsTrigger key={rg} value={rg} data-testid={`price-timeframe-${rg}`} className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-card data-[state=active]:shadow">
                  {rg}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <AnimatePresence>
        {overlay && diffPct != null && (
          <motion.div
            key="fv-summary"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="mb-2 sm:mb-2 flex flex-wrap items-center gap-2 text-[11px]"
            data-testid="price-chart-difference-chip"
          >
            {/* Verdict chip — always visible. On mobile it is intentionally
                larger and a touch louder so it anchors the section. */}
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 sm:px-2.5 sm:py-1 text-[12.5px] sm:text-[11px] font-semibold"
              style={{ color: diffColor, background: `${diffColor}24`, border: `1px solid ${diffColor}66` }}
            >
              {isUnder ? <ArrowUpRight className="w-3.5 h-3.5 sm:w-3 sm:h-3" /> : isOver ? <ArrowDownRight className="w-3.5 h-3.5 sm:w-3 sm:h-3" /> : null}
              {verdict.text}
            </span>
            {/* Desktop-only legend / micro-explainers. On mobile these are
                replaced by the larger chart, larger chip, and a compact
                two-item legend rendered BELOW the chart. */}
            <span className="hidden sm:inline-flex items-center gap-1.5 text-muted-foreground">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "rgba(63,174,137,0.34)", border: "1px solid rgba(63,174,137,0.55)" }} />
              {t("analyze.chart.legend_cheap")}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-muted-foreground">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "rgba(229,72,77,0.22)", border: "1px solid rgba(229,72,77,0.5)" }} />
              {t("analyze.chart.legend_expensive")}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-muted-foreground">
              <span className="w-3 h-0.5 rounded-sm" style={{ background: C_GREEN, boxShadow: "0 0 6px rgba(63,174,137,0.8)" }} />
              {t("analyze.chart.legend_fair_value")}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-muted-foreground">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: C_PRICE, border: "2px solid #0A0A0C" }} />
              {t("analyze.chart.legend_current_price")}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1 ml-auto text-muted-foreground/80 italic">
              {t("analyze.chart.legend_footnote")}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative" aria-label={`${ticker} price chart with dynamic fair value overlay`}>
        {loading && <div className="absolute inset-0 z-10 scan-sweep rounded-md" />}
        {/* Phase 6.AX — Chart fallback surface.
            When the backend reports that historical price data is
            unavailable (plan-restricted, rate-limited, or empty), render
            an honest, non-misleading placeholder INSTEAD of a blank
            canvas. The fair-value summary chip above still renders so
            the user can see the valuation conclusion. */}
        {!loading && series.length === 0 && chartStatus !== "ok" ? (
          <div
            className="flex flex-col items-center justify-center text-center rounded-lg border border-border/60 bg-muted/10 px-6 py-10"
            style={{ minHeight: Math.max(effectiveHeight - 60, 220) }}
            data-testid="price-chart-fallback"
            role="status"
            aria-live="polite"
          >
            <Info className="w-5 h-5 mb-2 text-muted-foreground" aria-hidden="true" />
            <div className="text-sm font-medium" data-testid="price-chart-fallback-title">
              {chartStatus === "unavailable_plan_restricted"
                ? t("analyze.chart.fallback_plan_restricted")
                : chartStatus === "unavailable_rate_limited"
                  ? t("analyze.chart.fallback_rate_limited")
                  : chartStatus === "unavailable_empty"
                    ? t("analyze.chart.fallback_empty")
                    : t("analyze.chart.fallback_unavailable")}
            </div>
            {chartUnavailableReason ? (
              <div className="mt-1.5 text-xs text-muted-foreground max-w-md" data-testid="price-chart-fallback-reason">
                {chartUnavailableReason}
              </div>
            ) : null}
            <div className="mt-3 text-[11px] text-muted-foreground/80 max-w-md">
              {t("analyze.chart.fallback_secondary")}
            </div>
          </div>
        ) : (
        <ResponsiveContainer width="100%" height={effectiveHeight}>
          <ComposedChart data={data} margin={{ top: 12, right: isMobile ? 86 : 130, left: 0, bottom: isMobile ? 4 : 0 }}>
            <defs>
              <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#DDE1E4" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#DDE1E4" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="gap-under" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C_GREEN} stopOpacity={0.45} />
                <stop offset="50%" stopColor={C_GREEN} stopOpacity={0.30} />
                <stop offset="100%" stopColor={C_GREEN} stopOpacity={0.45} />
              </linearGradient>
              <linearGradient id="gap-over" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C_RED} stopOpacity={0.45} />
                <stop offset="50%" stopColor={C_RED} stopOpacity={0.30} />
                <stop offset="100%" stopColor={C_RED} stopOpacity={0.45} />
              </linearGradient>
              <linearGradient id="gap-fair" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C_AMBER} stopOpacity={0.40} />
                <stop offset="100%" stopColor={C_AMBER} stopOpacity={0.40} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#242530" strokeOpacity={0.6} vertical={false} />
            <XAxis dataKey="t" tick={{ fill: "#7D8592", fontSize: isMobile ? 10 : 11 }} axisLine={false} tickLine={false} tickFormatter={xTickFormatter} minTickGap={isMobile ? 44 : 32} />
            <YAxis
              tick={{ fill: "#7D8592", fontSize: isMobile ? 10 : 11 }}
              axisLine={false} tickLine={false}
              width={isMobile ? 40 : 48}
              domain={overlay ? [yMin, yMax] : ["auto", "auto"]}
              allowDataOverflow={false}
            />
            <Tooltip content={<ChartTooltip fairValueLatest={fairValue} t={t} />} cursor={{ stroke: "#3A8573", strokeOpacity: 0.35 }} />

            {/* Cheap zone tint (below FV) */}
            {overlay ? (
              <Area
                type="monotone"
                dataKey="fv"
                baseValue={yMin}
                stroke="none"
                fill="rgba(63,174,137,0.16)"
                fillOpacity={1}
                isAnimationActive={false}
                connectNulls
              />
            ) : null}
            {/* Expensive zone tint (above FV) — slightly softened red so
                the "expensive" overlay stays informative without dominating
                the canvas (Phase 6.Y polish). */}
            {overlay ? (
              <Area
                type="monotone"
                dataKey="fv"
                baseValue={yMax}
                stroke="none"
                fill="rgba(229,72,77,0.10)"
                fillOpacity={1}
                isAnimationActive={false}
                connectNulls
              />
            ) : null}

            {/* Glow halo behind FV curve — slightly brighter for better
                fair-value emphasis. */}
            {overlay ? (
              <Line
                type="monotone"
                dataKey="fv"
                stroke={C_GREEN}
                strokeOpacity={0.28}
                strokeWidth={10}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ) : null}
            {/* FV curve solid */}
            {overlay ? (
              <Line
                type="monotone"
                dataKey="fv"
                stroke={C_GREEN}
                strokeWidth={2.4}
                dot={false}
                isAnimationActive
                connectNulls
              />
            ) : null}

            {/* Price line — slightly thicker stroke for smoother visual
                readability without changing the curve interpolation. */}
            <Area type="monotone" dataKey="price" stroke="#DDE1E4" strokeWidth={1.8} fill="url(#priceGrad)" isAnimationActive />

            {/* === GAP HIGHLIGHT === bridges the visual distance between price & fair value */}
            {overlay && gapStartT && lastT && currentPrice != null && fairValue != null ? (
              <ReferenceArea
                x1={gapStartT}
                x2={lastT}
                y1={Math.min(currentPrice, Number(fairValue))}
                y2={Math.max(currentPrice, Number(fairValue))}
                fill={`url(#${gapGradientId})`}
                stroke={gapColor}
                strokeOpacity={0.55}
                strokeDasharray="2 3"
                ifOverflow="extendDomain"
                label={(p) => <GapDeltaLabel {...p} color={gapColor} diffPct={diffPct} />}
              />
            ) : null}

            {/* Current price endpoint with NOW pill */}
            {overlay && lastT && currentPrice != null ? (
              <ReferenceDot
                x={lastT}
                y={currentPrice}
                r={1}
                ifOverflow="extendDomain"
                isFront
                shape={(props) => (
                  <CurrentPriceMarker
                    cx={props.cx} cy={props.cy}
                    currentPrice={currentPrice}
                    compact={isMobile}
                  />
                )}
              />
            ) : null}

            {/* Fair value endpoint with FAIR VALUE pill + glow */}
            {overlay && lastT && fairValue != null ? (
              <ReferenceDot
                x={lastT}
                y={Number(fairValue)}
                r={1}
                ifOverflow="extendDomain"
                isFront
                shape={(props) => (
                  <FairValueMarker
                    cx={props.cx} cy={props.cy}
                    fairValue={Number(fairValue)}
                    compact={isMobile}
                  />
                )}
              />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
        )}
      </div>

      {/* === Mobile-only controls — placed BELOW the chart so the chart owns
            the vertical space at the top. Larger tap targets, premium feel. */}
      <div className="sm:hidden mt-4 space-y-3" data-testid="price-chart-mobile-controls">
        {/* Compact 2-item mobile legend — only the high-value tokens */}
        {overlay ? (
          <div className="flex items-center justify-center gap-4 text-[11.5px] text-muted-foreground" data-testid="price-chart-mobile-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-0.5 rounded-sm" style={{ background: C_GREEN, boxShadow: "0 0 6px rgba(63,174,137,0.8)" }} />
              {t("analyze.chart.legend_fair_value_short")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: C_PRICE, border: "2px solid #0A0A0C" }} />
              {t("analyze.chart.legend_current_price")}
            </span>
          </div>
        ) : null}

        {/* Timeframe selector — larger, easier to tap on mobile */}
        <Tabs value={range} onValueChange={setRange} data-testid="price-timeframe-tabs-mobile">
          <TabsList className="w-full bg-muted/30 rounded-xl p-1 grid grid-cols-6 gap-1 h-auto">
            {RANGES.map((rg) => (
              <TabsTrigger
                key={rg}
                value={rg}
                data-testid={`price-timeframe-mobile-${rg}`}
                className="text-[13.5px] font-medium py-3 min-h-[44px] rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-[0_0_0_1px_rgba(63,174,137,0.32),0_0_14px_rgba(63,174,137,0.18)] data-[state=active]:text-foreground text-muted-foreground/85 transition-colors"
              >
                {rg}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Fair value overlay toggle — pulled down here on mobile */}
        <div className="flex items-center justify-between rounded-xl border border-border/60 bg-secondary/30 px-3 py-2.5">
          <Label htmlFor="fv-overlay-mobile" className="text-[13px] text-foreground/90 flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-0.5" style={{ background: C_GREEN, boxShadow: "0 0 5px rgba(63,174,137,0.8)" }} />
            {t("analyze.chart.overlay_label_mobile")}
          </Label>
          <Switch id="fv-overlay-mobile" checked={overlay} onCheckedChange={setOverlay} data-testid="fair-value-overlay-switch-mobile" />
        </div>
      </div>

      <div className="mt-3 sm:mt-3 flex items-start gap-2 text-[11px] text-muted-foreground/90" data-testid="price-chart-microexplanation">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#3182CE]" />
        {/* Desktop footnote — full sentence. Mobile footnote — compressed
            single-clause version so the high-value chart owns the screen. */}
        <span
          className="hidden sm:inline"
          dangerouslySetInnerHTML={{ __html: t("analyze.chart.microexplanation_desktop") }}
        />
        <span
          className="sm:hidden"
          dangerouslySetInnerHTML={{ __html: t("analyze.chart.microexplanation_mobile") }}
        />
      </div>
    </div>
  );
}
